import { map, pipe, reverse, toPairs, when } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import { IntlShape, useIntl } from "react-intl";
import { ExpandedFormats } from "@sinch/core/hooks/format.types";

type NameFormat = "abbreviated" | "initials" | "hidden" | "regular";
export interface NameFormatOptions {
  name?: NameFormat;
  surname?: NameFormat;
  order?: "regular" | "reversed";
}

interface NameParts {
  name?: string | null;
  surname?: string | null;
}

export const useFormatName = () => {
  const { formats } = useIntl() as IntlShape & {
    formats: ExpandedFormats;
  };

  return (value: NameParts, { format }: { format?: keyof ExpandedFormats["name"] }) => {
    const formatVariant: NameFormatOptions = format ? formats.name[format] : {};

    const formattedName = pipe(
      toPairs<NameParts, keyof NameParts>,
      map(([key, val]) => nameFormat(val, formatVariant[key as keyof NameParts])),
      (list) => list.filter(isNotNilOrEmpty),
      when(() => formatVariant.order === "reversed", reverse<string | null | undefined>)
    )(value);

    if (formatVariant.name === "initials" && formatVariant.surname === "initials") {
      return formattedName.join("");
    }
    return formattedName.join(" ");
  };
};

const nameFormat = (value: string | null | undefined, variant: NameFormat | undefined) => {
  if (!value) {
    return null;
  }
  if (!variant) {
    return value;
  }
  switch (variant) {
    case "abbreviated":
      return value?.[0] + ".";
    case "initials":
      return value?.[0];
    case "hidden":
      return null;
    default:
      return value;
  }
  return value;
};
